import { SequenceDetail, CreatorSequencesResponse, HolidayType, SoftwareType } from '../types/sequence';
import { supabase } from '@/integrations/supabase/client';
import { 
  SAINT_NICK_USER_ID, 
  SAINT_NICK_DISPLAY_NAME, 
  isSaintNickAccount 
} from '../utils/constants';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.lighthunt.com';
const IS_DEVELOPMENT = import.meta.env.DEV;

export const getSequenceDetails = async (id: string): Promise<SequenceDetail> => {
  try {
    // Fetch sequence with related data using joins
    const { data: sequence, error: sequenceError } = await supabase
      .from('sequences')
      .select(`
        *,
        artists(
          id,
          name,
          description,
          image_url
        ),
        genres(
          id,
          name,
          description
        ),
        displays(
          id,
          name,
          description,
          location,
          latitude,
          longitude,
          average_rating,
          rating_count,
          user_id
        ),
        sequence_lor_meta(
          controller_type,
          channel_count_ac,
          software_version,
          prop_types,
          license_level_required,
          sequence_type_tags,
          import_notes
        )
      `)
      .eq('id', id)
      .single();

    if (sequenceError) {
      console.error('Error fetching sequence:', sequenceError);
      throw new Error(`Failed to fetch sequence: ${sequenceError.message}`);
    }

    if (!sequence) {
      throw new Error('Sequence not found');
    }

    console.log('Retrieved sequence from Supabase:', sequence);

    // Look up the creator's public profile for unified identity across the app
    let creatorProfile: {
      user_id: string;
      username: string | null;
      display_name: string | null;
      avatar_url: string | null;
      created_at: string | null;
    } | null = null;

    if (sequence.user_id) {
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('user_id, username, display_name, avatar_url, created_at')
        .eq('user_id', sequence.user_id)
        .maybeSingle();

      if (profileError) {
        console.warn('Failed to load creator profile for sequence:', profileError);
      }

      creatorProfile = profileData || null;
    }

    // Enhanced artist name lookup with multiple fallback strategies
    const getArtistName = (seq: any): string => {
      // First try: Direct artist join from database
      if (seq.artists?.name && seq.artists.name.trim() !== '') {
        return seq.artists.name;
      }
      
      // Second try: Check if artist name is embedded in title
      // Common patterns: "Artist - Song Title" or "Song Title by Artist"
      if (seq.title) {
        const title = seq.title.toString();
        
        // Pattern: "Artist - Song Title"
        const dashMatch = title.match(/^(.+?)\s*-\s*(.+)$/);
        if (dashMatch && dashMatch[1].trim() !== '' && !dashMatch[1].toLowerCase().includes('sequence')) {
          return dashMatch[1].trim();
        }
        
        // Pattern: "Song Title by Artist"
        const byMatch = title.match(/^(.+?)\s+by\s+(.+)$/i);
        if (byMatch && byMatch[2].trim() !== '') {
          return byMatch[2].trim();
        }
      }
      
      // Third try: Check if name field has artist info
      if (seq.name && seq.name !== seq.title) {
        const name = seq.name.toString();
        const dashMatch = name.match(/^(.+?)\s*-\s*(.+)$/);
        if (dashMatch && dashMatch[1].trim() !== '' && !dashMatch[1].toLowerCase().includes('sequence')) {
          return dashMatch[1].trim();
        }
      }
      
      // Final fallback: Unknown Artist
      return 'Unknown Artist';
    };

    // Transform LOR metadata if it exists
    let lorMetadata: SequenceDetail['lor_metadata'] = undefined;
    if (sequence.sequence_lor_meta && Array.isArray(sequence.sequence_lor_meta) && sequence.sequence_lor_meta.length > 0) {
      const lorData = sequence.sequence_lor_meta[0];
      lorMetadata = {
        controller_type: lorData.controller_type,
        channel_count_ac: lorData.channel_count_ac || undefined,
        software_version: lorData.software_version || undefined,
        prop_types: lorData.prop_types || undefined,
        license_level_required: lorData.license_level_required || undefined,
        sequence_type_tags: lorData.sequence_type_tags || undefined,
        import_notes: lorData.import_notes || undefined,
      };
    }

    // Transform the data to match our expected interface
    const sequenceDetail: SequenceDetail = {
      id: sequence.id,
      title: sequence.title || sequence.name,
      description: sequence.description || '',
      price: parseFloat(sequence.price || '0'),
      isFree: sequence.is_free || false,
      software: sequence.software_type as SoftwareType,
      duration: sequence.duration || 0,
      downloadCount: (sequence.download_count || 0) + (sequence.download_baseline || 0),
      featured: sequence.featured || false,
      imageUrl: sequence.album_cover_url || sequence.image_url || '',
      videoUrl: sequence.video_url || '',
      fileUrl: sequence.storage_path || '',
      createdAt: sequence.created_at,
      updatedAt: sequence.updated_at,
      channelCount: sequence.channel_count || undefined,
      file_type: sequence.file_type || undefined,
      lor_metadata: lorMetadata,

      // Song information from the sequence itself
      song: {
        id: sequence.id,
        title: sequence.title || sequence.name,
        artist: getArtistName(sequence),
        genre: sequence.primary_genre ||
               (Array.isArray(sequence.genres) && sequence.genres.length > 0 ? sequence.genres[0] : undefined) ||
               'Unknown Genre',
        album: sequence.album || '',
        duration: sequence.duration || 0,
        imageUrl: sequence.album_cover_url || sequence.image_url || '',
        videoUrl: sequence.video_url || '',
        // Spotify data
        spotifyTrackId: sequence.spotify_track_id || '',
        spotifyPreviewUrl: sequence.spotify_preview_url || '',
        spotifyExplicit: sequence.spotify_explicit || false,
        spotifyDurationMs: sequence.spotify_duration_ms || 0,
        spotifyPopularity: sequence.spotify_popularity || 0,
      },

      // Display information
      display: sequence.displays ? {
        id: sequence.displays.id.toString(),
        name: sequence.displays.name,
        description: sequence.displays.description || '',
        location: sequence.displays.location || '',
        latitude: sequence.displays.latitude || 0,
        longitude: sequence.displays.longitude || 0,
        averageRating: parseFloat(sequence.displays.average_rating?.toString() || '0'),
        ratingCount: sequence.displays.rating_count || 0,
        creator: {
          id: sequence.displays.user_id || '',
          username: 'Display Owner',
          email: '',
          firstName: '',
          lastName: '',
          avatarUrl: '',
          createdAt: '',
        }
      } : {
        id: '0',
        name: 'Unknown Display',
        description: '',
        location: 'Unknown Location',
        latitude: 0,
        longitude: 0,
        averageRating: 0,
        ratingCount: 0,
        creator: {
          id: '',
          username: 'Unknown User',
          email: '',
          firstName: '',
          lastName: '',
          avatarUrl: '',
          createdAt: '',
        }
      },

      // Creator info - unified from user_profiles (fallbacks preserved)
      creator: {
        id: sequence.user_id || 'unknown',
        username: (() => {
          if (!sequence.user_id) return 'Unknown Creator';
          if (isSaintNickAccount(sequence.user_id)) return SAINT_NICK_DISPLAY_NAME;
          return (
            creatorProfile?.username ||
            'Sequence Creator'
          );
        })(),
        email: '',
        firstName: '',
        lastName: '',
        avatarUrl: creatorProfile?.avatar_url || '',
        createdAt: creatorProfile?.created_at || sequence.created_at || '',
      },

      // Rating info - we'll need to implement this later
      averageRating: 0,
      ratingCount: 0,
      ratingDistribution: {
        1: 0,
        2: 0,
        3: 0,
        4: 0,
        5: 0,
      },

      // Props - we'll implement this later when the RPC function exists
      props: [],

      // Additional fields
      holiday: (sequence.holiday_type || 'Christmas') as HolidayType, // Use holiday_type from database
      tags: [],
      isDownloaded: false,
      isFavorited: false,
      
      // Ownership information
      ownership: {
        claimStatus: sequence.claim_status || 'unclaimed',
        originalCreatorInfo: sequence.original_creator_info || undefined,
        claimRequestedAt: sequence.claim_requested_at || undefined,
        claimVerifiedAt: sequence.claim_verified_at || undefined,
      },
    };

    return sequenceDetail;

  } catch (error) {
    console.error('Error in getSequenceDetails:', error);
    throw error;
  }
};

export const getCreatorSequences = async (creatorId: string, limit: number = 10): Promise<CreatorSequencesResponse> => {
  try {
    console.log('Fetching sequences for creator:', creatorId);

    // If no valid creator ID, return empty results
    if (!creatorId || creatorId.trim() === '' || creatorId === 'null' || creatorId === 'unknown') {
      console.log('No valid creator ID provided, returning empty results');
      return {
        sequences: [],
        total: 0,
        hasMore: false,
      };
    }

    // Fetch sequences by the creator
    const { data: sequences, error } = await supabase
      .from('sequences')
      .select(`
        *,
        artists(
          id,
          name
        ),
        genres(
          id,
          name
        ),
        displays(
          id,
          name
        )
      `)
      .eq('user_id', creatorId)
      .limit(limit);

    if (error) {
      console.error('Error fetching creator sequences:', error);
      throw new Error(`Failed to fetch creator sequences: ${error.message}`);
    }

    console.log('Retrieved creator sequences from Supabase:', sequences);

    // Enhanced artist name lookup with multiple fallback strategies
    const getArtistName = (seq: any): string => {
      // First try: Direct artist join from database
      if (seq.artists?.name && seq.artists.name.trim() !== '') {
        return seq.artists.name;
      }
      
      // Second try: Check if artist name is embedded in title
      // Common patterns: "Artist - Song Title" or "Song Title by Artist"
      if (seq.title) {
        const title = seq.title.toString();
        
        // Pattern: "Artist - Song Title"
        const dashMatch = title.match(/^(.+?)\s*-\s*(.+)$/);
        if (dashMatch && dashMatch[1].trim() !== '' && !dashMatch[1].toLowerCase().includes('sequence')) {
          return dashMatch[1].trim();
        }
        
        // Pattern: "Song Title by Artist"
        const byMatch = title.match(/^(.+?)\s+by\s+(.+)$/i);
        if (byMatch && byMatch[2].trim() !== '') {
          return byMatch[2].trim();
        }
      }
      
      // Third try: Check if name field has artist info
      if (seq.name && seq.name !== seq.title) {
        const name = seq.name.toString();
        const dashMatch = name.match(/^(.+?)\s*-\s*(.+)$/);
        if (dashMatch && dashMatch[1].trim() !== '' && !dashMatch[1].toLowerCase().includes('sequence')) {
          return dashMatch[1].trim();
        }
      }
      
      // Final fallback: Unknown Artist
      return 'Unknown Artist';
    };

    // Also fetch the creator's profile once for display information
    let creatorProfile: { username: string | null; display_name: string | null; avatar_url: string | null } | null = null;
    if (creatorId) {
      const { data: profileData } = await supabase
        .from('user_profiles')
        .select('username, display_name, avatar_url')
        .eq('user_id', creatorId)
        .maybeSingle();
      creatorProfile = profileData || null;
    }

    // Transform sequences to match expected format
    const transformedSequences = (sequences || []).map(seq => ({
      id: seq.id,
      title: seq.title || seq.name,
      displayName: seq.displays?.name || 'Unknown Display',
      imageUrl: seq.album_cover_url || seq.image_url || '',
      price: parseFloat(seq.price || '0'),
      isFree: seq.is_free || false,
      software: seq.software_type as SoftwareType,
      holiday: 'Christmas' as HolidayType, // We'll determine this properly later
      song: {
        id: seq.id,
        title: seq.title || seq.name,
        artist: getArtistName(seq),
        genre: seq.primary_genre ||
               (Array.isArray(seq.genres) && seq.genres.length > 0 ? seq.genres[0] : undefined) ||
               'Unknown Genre',
        album: seq.album || '',
        duration: seq.duration || 0,
        imageUrl: seq.album_cover_url || seq.image_url || '',
        videoUrl: seq.video_url || '',
        spotifyTrackId: seq.spotify_track_id || '',
        spotifyPreviewUrl: seq.spotify_preview_url || '',
        spotifyExplicit: seq.spotify_explicit || false,
        spotifyDurationMs: seq.spotify_duration_ms || 0,
        spotifyPopularity: seq.spotify_popularity || 0,
      },
      createdAt: seq.created_at || '',
      downloadCount: (seq.download_count || 0) + (seq.download_baseline || 0),
      averageRating: 0,
      ratingCount: 0,
      featured: seq.featured || false,
      userId: seq.user_id || creatorId,
      creatorId: seq.user_id || creatorId,
      creatorName: (creatorProfile?.display_name || creatorProfile?.username || undefined),
      creatorAvatar: creatorProfile?.avatar_url || undefined,
    }));

    return {
      sequences: transformedSequences,
      total: sequences?.length || 0,
      hasMore: (sequences?.length || 0) === limit,
    };

  } catch (error) {
    console.error('Error in getCreatorSequences:', error);
    throw error;
  }
};